/**
 * Runtime API Client Configuration
 *
 * Axios instance for communicating with the Runtime API.
 * Uses the same authentication tokens as the Tenant API.
 */

import axios from "axios";
import type { AxiosInstance, AxiosError, InternalAxiosRequestConfig, AxiosResponse } from "axios";
import { getAccessToken } from "../lib/utils/token";

// ============================================================================
// AXIOS INSTANCE CREATION
// ============================================================================

/**
 * Create and configure Axios instance for Runtime API
 */
const runtimeApiClient: AxiosInstance = axios.create({
  baseURL: import.meta.env.VITE_RUNTIME_API_BASE_URL || "https://runtime.dev.ketrics.io",
  timeout: 30000,
  headers: {
    "Content-Type": "application/json",
  },
});

// ============================================================================
// REQUEST INTERCEPTOR
// ============================================================================

/**
 * Request interceptor: Add auth token
 */
runtimeApiClient.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const token = getAccessToken();

    // Add authorization header if token exists
    if (token && config.headers) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    // Log request in development mode
    if (import.meta.env.DEV) {
      console.log(`[Runtime API Request] ${config.method?.toUpperCase()} ${config.url}`, {
        params: config.params,
        data: config.data,
      });
    }

    return config;
  },
  (error: AxiosError) => {
    console.error("[Runtime API Request Error]", error);
    return Promise.reject(error);
  },
);

// ============================================================================
// RESPONSE INTERCEPTOR
// ============================================================================

/**
 * Response interceptor: Handle errors globally
 */
runtimeApiClient.interceptors.response.use(
  (response: AxiosResponse) => {
    // Log response in development mode
    if (import.meta.env.DEV) {
      console.log(`[Runtime API Response] ${response.config.method?.toUpperCase()} ${response.config.url}`, {
        status: response.status,
        data: response.data,
      });
    }

    return response;
  },
  (error: AxiosError) => {
    // Log error in development mode
    if (import.meta.env.DEV) {
      console.error("[Runtime API Response Error]", {
        url: error.config?.url,
        method: error.config?.method,
        status: error.response?.status,
        data: error.response?.data,
      });
    }

    // Handle network errors
    if (!error.response) {
      console.error("[Runtime API] Network error: No response received");
    }

    return Promise.reject(error);
  },
);

// ============================================================================
// EXPORT
// ============================================================================

export default runtimeApiClient;
